import { Card } from "@mui/material";
import { useFormikContext } from "formik";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import { ConfirmationDialog } from "@/components/molecules/ConfirmationDialog";
import { CampaignFormData } from "@/components/screens/CampaignFormPage/types";
import useCampaignImage from "@/hooks/use-campaign-image";
import { CampaignFormMessages } from "@/messages/campaigns/campaign-form.messages";
import pxToRem from "@/themes/functions/pxToRem";

type Props = {
  messages: CampaignFormMessages;
};

function CampaignImage({ messages }: Props) {
  const useImage = useCampaignImage({ messages });
  const hasImages = useImage.campaignImages && useImage.campaignImages.length > 0;
  const { values } = useFormikContext<CampaignFormData>();
  const mainImage = hasImages ? useImage.campaignImages[0] : null;

  const hasFile = mainImage?.file ? true : false;
  const previewSrc = hasFile ? mainImage && (mainImage.preview as string) : "";
  const urlSrc = !hasFile && mainImage ? (mainImage.url as string) : "";

  const imageSrc = hasFile ? previewSrc : urlSrc;

  return (
    <Card
      sx={{
        "&:hover .card-header": {
          transform: "translate3d(0, -80px, 0)",
          zIndex: 10001,
        },
        height: "100%",
      }}
    >
      <MDBox
        position="relative"
        borderRadius="lg"
        mt={-3}
        mx={2}
        className={mainImage ? "card-header" : "none"}
        sx={{ transition: "transform 300ms cubic-bezier(0.34, 1.61, 0.7, 1)" }}
        height="300px"
        bgColor="white"
        {...useImage.getRootProps()}
      >
        {values.disabledField ? "" : <input {...useImage.getInputProps()} />}

        <MDBox
          borderRadius="lg"
          shadow="sm"
          width="100%"
          height="100%"
          position="relative"
          zIndex={10}
          mb={2}
          overflow="hidden"
          bgColor="white"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {mainImage ? (
            <img
              src={imageSrc || ""}
              alt="imagem da campanha"
              style={{
                objectFit: "contain",
                maxWidth: "100%",
                maxHeight: "100%",
                width: "auto",
                height: "auto",
              }}
            />
          ) : (
            <MDBox position="relative" borderRadius="lg" width="100%" height="100%">
              <MDBox
                borderRadius="lg"
                width="100%"
                sx={{ height: pxToRem(300) }}
                height="100%"
                position="relative"
                zIndex={10}
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
                flexDirection={"column"}
                mb={2}
                overflow="hidden"
              >
                <MDButton
                  disabled={values.disabledField}
                  variant="gradient"
                  color="dark"
                  size="small"
                >
                  {messages.uploadImageButton}
                </MDButton>
              </MDBox>
            </MDBox>
          )}
        </MDBox>
      </MDBox>

      <MDBox textAlign="center" pt={2} pb={3} px={3}>
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          mt={-10}
          position="relative"
          zIndex={1}
        >
          {mainImage && (
            <MDButton
              disabled={values.disabledField}
              variant="outlined"
              color="error"
              size="small"
              onClick={(event) => useImage.promptRemoveImage(event, mainImage)}
            >
              {messages.deleteImageButton}
            </MDButton>
          )}
        </MDBox>
      </MDBox>
      <ConfirmationDialog
        isOpen={useImage.isVisible}
        onClose={useImage.handleClose}
        onConfirm={useImage.handleConfirmDelete}
        titleText={messages.confirmDeleteTitle}
        descriptionText={messages.confirmDeleteDescription}
        cancelText={messages.cancelButton}
        confirmText={messages.deleteButton}
      />
    </Card>
  );
}

export default CampaignImage;
